import React, { useState, useEffect, useContext, useRef } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { Skeleton, Button, Dropdown, message as antdMessage, Input, InputNumber, Space, Checkbox, Modal, Select, Card, App, Descriptions, Tag } from "antd"
import { getRuns, getRunSettings, regenerateRunSettings, updateRun, getRunReports, getNextflowRunStatus, checkSkipReportStatus, setSkipReportCheck, deleteSkipReportCheck, resetRunningLog, checkRunErrorLog, checkNextflowLog, getBasecallerOutputLogs, deleteEmptyBasecallerLogs, cleanRunFolder, makePeek, showPeekResult, showPeekErrorLog, getReportMuxScansFromReport } from "../endpoints/runEndpoints"
import { getFlowCells, FlowCell } from "../endpoints/flowcellEndpoints"
import { getPositions, getProtocolRunInfo, getFlowCellInfo, getAcquisitionInfo, startProtocol, stopProtocol, updateRunUntil, getChannelStates } from "../endpoints/minknowEndpoints"
import { ContainerFull, NameValueRow } from "../components"
import type { RunInterface, RunStatusType } from "../types"
import { UserContext } from "../context"
import { isAdmin } from "../utils/userUtils"

const RUN_STATUS_OPTIONS = [
    { value: "new", label: "New" },
    { value: "running", label: "Running" },
    { value: "test", label: "Test" },
    { value: "completion-detected", label: "Completion Detected" },
    { value: "completed", label: "Completed" },
    { value: "canceled", label: "Canceled" },
]

export const RunDetails = (): JSX.Element => {
    const { runId } = useParams<{ runId?: string }>()
    const navigate = useNavigate()

    // Local state for run data and loading/error
    const [run, setRun] = useState<RunInterface | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)

    // Editable fields
    const [status, setStatus] = useState<RunStatusType | undefined>(undefined)
    const [device, setDevice] = useState<string>("a6000")
    const [flowCellId, setFlowCellId] = useState<string>("")
    const [flowCellNumericId, setFlowCellNumericId] = useState<number | undefined>(undefined)
    const [pores, setPores] = useState<number | undefined>(undefined)
    const [duration, setDuration] = useState<number | undefined>(undefined)
    const [cellDied, setCellDied] = useState<boolean>(false)
    const [newCell, setNewCell] = useState<boolean>(false)
    const [used, setUsed] = useState<boolean>(true)
    const [notes, setNotes] = useState<string>("")
    const { message: messageApi, modal } = App.useApp()
    const [showSuccess, setShowSuccess] = useState(false)
    
    // Flow cells data
    const [flowCells, setFlowCells] = useState<FlowCell[]>([])
    const [flowCellsLoading, setFlowCellsLoading] = useState(false)

    // Settings state
    const [showSettings, setShowSettings] = useState(false)
    const [settings, setSettings] = useState<string>()
    const [settingsLoading, setSettingsLoading] = useState(false)
    const [settingsError, setSettingsError] = useState<string | null>(null)

    // Instrument reports state
    const [reports, setReports] = useState<string[]>([])
    const [reportsLoading, setReportsLoading] = useState(false)
    const [reportsError, setReportsError] = useState<string | null>(null)

    // RBK run state
    const [rbkDevice, setRbkDevice] = useState<'a6000' | 'mk1c' | 'outpond' | 'sierra'>('a6000')
    const rbkDeviceInitialized = useRef(false)
    const [rbkPosition, setRbkPosition] = useState<string>('')
    const [positionLockedByFlowcell, setPositionLockedByFlowcell] = useState<boolean>(false)
    const [availablePositions, setAvailablePositions] = useState<string[]>([])
    const [rbkHours, setRbkHours] = useState<number | null>(null)
    const [rbkRunning, setRbkRunning] = useState(false)
    const [rbkError, setRbkError] = useState<string | null>(null)
    const [runUntilHours, setRunUntilHours] = useState<number | null>(null)
    const [acquisitionLoading, setAcquisitionLoading] = useState(false)
    const [acquisitionData, setAcquisitionData] = useState<any>(null)
    const [acquisitionError, setAcquisitionError] = useState<string | null>(null)

    // Channel states
    const [channelStatesLoading, setChannelStatesLoading] = useState(false)
    const [channelStatesData, setChannelStatesData] = useState<any[] | null>(null)
    const [channelStatesError, setChannelStatesError] = useState<string | null>(null)
    const [reportMuxChannelLoading, setReportMuxChannelLoading] = useState(false)

    // Stop protocol
    const [stopProtocolLoading, setStopProtocolLoading] = useState(false)

    // Make peek
    const [makePeekLoading, setMakePeekLoading] = useState(false)

    // Show peek result
    const [peekResultLoading, setPeekResultLoading] = useState(false)
    const [peekResultOrders, setPeekResultOrders] = useState<{orderId:string, files:{resultHtml:string|null, zip:string|null, errorLog:string|null}}[] | null>(null)
    const [peekResultMessage, setPeekResultMessage] = useState<string | null>(null)
    const [peekResultTotalReads, setPeekResultTotalReads] = useState<number | null>(null)
    const [peekErrorLogExists, setPeekErrorLogExists] = useState<boolean>(false)
    
    // Nextflow status
    const [nextflowStatus, setNextflowStatus] = useState<string>("not running")
    const [nextflowStatusLoading, setNextflowStatusLoading] = useState<boolean>(false)
    const [skipReportCheck, setSkipReportCheckState] = useState<boolean>(false)
    const [skipReportCheckLoading, setSkipReportCheckLoading] = useState<boolean>(false)

    // Nextflow log status
    const [nextflowLogStatus, setNextflowLogStatus] = useState<'checking' | 'exists' | 'not-exists' | 'server-off'>('checking')

    // Basecaller output logs state
    const [basecallerOutputStatus, setBasecallerOutputStatus] = useState<'checking' | 'exists' | 'not-exists' | 'server-off'>('checking')
    const [basecallerOutputFiles, setBasecallerOutputFiles] = useState<string[]>([])
    const [deleteEmptyLogsLoading, setDeleteEmptyLogsLoading] = useState(false)

    // Error log status
    const [errorLogStatus, setErrorLogStatus] = useState<'checking' | 'exists' | 'not-exists' | 'server-off'>('checking')

    // Flowcell check progress state
    const [flowcellCheckLoading, setFlowcellCheckLoading] = useState(false)
    const [flowcellCheckData, setFlowcellCheckData] = useState<any>(null)
    const [flowcellCheckError, setFlowcellCheckError] = useState<string | null>(null)

    // Sync data state
    const [syncLoading, setSyncLoading] = useState(false)

    // Clean run folder state
    const [cleanRunFolderLoading, setCleanRunFolderLoading] = useState(false)
    const [resetRunningLogLoading, setResetRunningLogLoading] = useState(false)

    // Basecaller log summarize modal state
    const [summarizeModalOpen, setSummarizeModalOpen] = useState(false)
    const [summarizeModalFileName, setSummarizeModalFileName] = useState<string | null>(null)
    const [summarizeModalLoading, setSummarizeModalLoading] = useState(false)
    const [summarizeModalError, setSummarizeModalError] = useState<string | null>(null)
    const [summarizeModalData, setSummarizeModalData] = useState<{
        simplexBasecalled: number[]
        simplexFiltered: number[]
        simplexUnclassified: number[]
        barcodeTotal: number
        barcodeCounts: { name: string; count: number }[]
    } | null>(null)

    // User context for admin check
    const userContext = useContext(UserContext)
    const canResetRunningLog = userContext.user?.userRole === 'admin' || userContext.user?.userRole === 'scientist'

    // const prevRunId = useRef<string | undefined>(undefined)
    // const [saving, setSaving] = useState(false)

    // Fetch flow cells list
    useEffect(() => {
        if (!isAdmin(userContext.user)) return
        
        setFlowCellsLoading(true)
        getFlowCells()
            .then((data) => {
                if (data.code === 1000 && data.flowcells) {
                    setFlowCells(data.flowcells)
                }
                setFlowCellsLoading(false)
            })
            .catch((e) => {
                console.error('Failed to fetch flow cells:', e)
                setFlowCellsLoading(false)
            })
    }, [userContext.user])

    // Fetch Nextflow status
    useEffect(() => {
        const fetchNextflowStatus = async () => {
            if (!runId) return
            
            setNextflowStatusLoading(true)
            try {
                const response = await getNextflowRunStatus({ runId: Number(runId) })
                if (response.code === 1000 && response.status) {
                    setNextflowStatus(response.status)
                }
            } catch (err) {
                console.error("Failed to fetch Nextflow status:", err)
                setNextflowStatus("not running")
            } finally {
                setNextflowStatusLoading(false)
            }
        }
        
        fetchNextflowStatus()
        // Poll every 30 seconds
        const interval = setInterval(fetchNextflowStatus, 30000)
        return () => clearInterval(interval)
    }, [runId])

    useEffect(() => {
        const fetchSkipReportStatus = async () => {
            if (!runId) return

            setSkipReportCheckLoading(true)
            try {
                const response = await checkSkipReportStatus({ runId: Number(runId) })
                if (response.code === 1000) {
                    setSkipReportCheckState(Boolean(response.exists))
                } else {
                    setSkipReportCheckState(false)
                }
            } catch (err) {
                console.error("Failed to check skip report status:", err)
                setSkipReportCheckState(false)
            } finally {
                setSkipReportCheckLoading(false)
            }
        }

        fetchSkipReportStatus()
    }, [runId])

    // Fetch nextflow log status
    useEffect(() => {
        const fetchNextflowLogStatus = async () => {
            if (!runId) return

            setNextflowLogStatus('checking')
            try {
                const response = await checkNextflowLog({ runId: Number(runId) })
                if (response.code === 1000) {
                    setNextflowLogStatus('exists')
                } else if (response.code === 1001) {
                    setNextflowLogStatus('not-exists')
                } else {
                    setNextflowLogStatus('server-off')
                }
            } catch (err) {
                console.error("Failed to check nextflow log:", err)
                setNextflowLogStatus('server-off')
            }
        }

        fetchNextflowLogStatus()
    }, [runId])

    // Fetch basecaller output logs
    useEffect(() => {
        const fetchBasecallerOutputLogs = async () => {
            if (!runId) return

            setBasecallerOutputStatus('checking')
            setBasecallerOutputFiles([])
            try {
                const response = await getBasecallerOutputLogs({ runId: Number(runId) })
                if (response.code === 1000) {
                    setBasecallerOutputStatus('exists')
                    setBasecallerOutputFiles(response.files || [])
                } else if (response.code === 1001) {
                    setBasecallerOutputStatus('not-exists')
                } else {
                    setBasecallerOutputStatus('server-off')
                }
            } catch (err) {
                console.error("Failed to fetch basecaller output logs:", err)
                setBasecallerOutputStatus('server-off')
            }
        }

        fetchBasecallerOutputLogs()
    }, [runId])

    // Fetch error log status
    useEffect(() => {
        const fetchErrorLogStatus = async () => {
            if (!runId) return
            
            setErrorLogStatus('checking')
            try {
                const response = await checkRunErrorLog({ runId: Number(runId) })
                if (response.code === 1000) {
                    setErrorLogStatus('exists')
                } else if (response.code === 1001) {
                    setErrorLogStatus('not-exists')
                } else {
                    setErrorLogStatus('server-off')
                }
            } catch (err) {
                console.error("Failed to check error log:", err)
                setErrorLogStatus('server-off')
            }
        }
        
        fetchErrorLogStatus()
    }, [runId])

    // Fetch run data on mount or when runId changes
    useEffect(() => {
        let ignore = false
        setLoading(true)
        setError(null)
        getRuns({ type: "one", runId: Number(runId) })
            .then((data) => {
                if (ignore) return
                if (data.code !== 1000 || !data.runs || data.runs.length === 0) {
                    setError(data.message || "No run found")
                    setRun(null)
                    setLoading(false)
                    return
                }
                setRun(data.runs[0])
                // console.log("Fetched run:", data.runs[0])
                setLoading(false)
            })
            .catch((e) => {
                if (ignore) return
                setError(e.message || "Failed to fetch run")
                setLoading(false)
            })
        return () => {
            ignore = true
        }
    }, [runId])

    // Sync editable fields with run data
    useEffect(() => {
        if (!run) return

        setStatus(run.status)
        setDevice(run.device ?? "a6000")
        if (!rbkDeviceInitialized.current) {
            const newDevice = (run.device ?? "a6000") as 'a6000' | 'mk1c' | 'outpond' | 'sierra'
            setRbkDevice(newDevice)
            rbkDeviceInitialized.current = true
        }
        setFlowCellId(run.flowCellId ?? "")
        setPores(run.pores ?? undefined)
        setDuration(run.duration ?? undefined)
        setCellDied(run.cell_died ?? false)
        setNewCell(run.new_cell ?? false)
        setUsed(run.used ?? true)
        setNotes(run.notes ?? "")
        // Only update if backend value is different from local state (e.g. after save from another tab)
        if (run.status !== status) setStatus(run.status)
        if ((run.flowCellId || "") !== flowCellId) setFlowCellId(run.flowCellId || "")
        if (run.pores !== pores) setPores(run.pores ?? undefined)
        if (run.duration !== duration) setDuration(run.duration ?? undefined)
        if (run.cell_died !== cellDied) setCellDied(run.cell_died ?? false)
        if (run.new_cell !== newCell) setNewCell(run.new_cell ?? false)
        if (run.used !== used) setUsed(run.used ?? true)
        if ((run.notes || "") !== notes) setNotes(run.notes || "")
        // eslint-disable-next-line
    }, [run])
    
    // Sync flow cell numeric ID when flowCellId or flowCells change
    useEffect(() => {
        if (flowCells.length === 0) return
        const flowCell = flowCells.find(fc => fc.name === flowCellId)
        if (flowCell) {
            setFlowCellNumericId(flowCell.id)
        } else {
            setFlowCellNumericId(undefined)
        }
    }, [flowCellId, flowCells])

    // Save handler for individual fields
    const handleFieldSave = async (
        fields: Partial<{ status: RunStatusType; device: string; flowCellId: string; pores: number; duration: number; cell_died: boolean; new_cell: boolean; used: boolean; notes: string }>
    ) => {
        if (!run?.id) return
        // setSaving(true)
        const res = await updateRun({
            runId: run.id,
            ...fields,
        })
        // setSaving(false)
        if (res.code !== 1000) {
            messageApi.error(res.message || "Failed to update run")
        } else {
            getRuns({ type: "one", runId: Number(runId) })
                .then((data) => {
                    if (data.code === 1000 && data.runs && data.runs.length > 0) {
                        setRun(data.runs[0])
                        setShowSuccess(true)
                    }
                })
                .catch(() => {})
        }
    }

    // Handler to fetch and show run settings
    const handleShowSettings = async () => {
        setShowSettings(true)
        setSettingsLoading(true)
        setSettingsError(null)
        try {
            // You may need to adjust the endpoint path as needed
            const res = await getRunSettings({ runId: Number(runId) })
            if (!res || res.code !== 1000) {
                throw new Error(res.message || "Failed to fetch run settings")
            }
            if (!res.settings) {
                setSettings(undefined)
                setSettingsLoading(false)
                return
            }
            setSettings(res.settings)
        } catch (e: any) {
            setSettings(`{error: ${e.message ?? "Failed to fetch run settings"}}`)
            setSettingsError(e.message || "Failed to fetch run settings")
        }
        setSettingsLoading(false)
    }


    // Handler to fetch and show run settings
    const handleRegenerateSettings = async () => {
        setShowSettings(true)
        setSettingsLoading(true)
        setSettingsError(null)
        try {
            // You may need to adjust the endpoint path as needed
            const res = await regenerateRunSettings({ runId: Number(runId) })
            if (!res || res.code !== 1000) {
                throw new Error(res.message || "Failed to regenerate run settings")
            }
            if (!res.settings) {
                setSettings(undefined)
                setSettingsLoading(false)
                return
            }
            setSettings(res.settings)
            messageApi.success("Run settings regenerated successfully")
            
        } catch (e: any) {
            setSettings(`{error: ${e.message ?? "Failed to regenerate run settings"}}`)
            setSettingsError(e.message || "Failed to fetch run settings")
        }
        setSettingsLoading(false)
    }

    useEffect(() => {
        if (showSuccess) {
            messageApi.success("Run updated")
            setShowSuccess(false)
        }
    }, [showSuccess, messageApi])

    const handleDeleteEmptyLogs = async (): Promise<void> => {
        if (!runId || deleteEmptyLogsLoading) return

        setDeleteEmptyLogsLoading(true)
        try {
            const response = await deleteEmptyBasecallerLogs({ runId: Number(runId) })
            if (response.code === 1000) {
                antdMessage.success(response.message || "Deleted empty basecaller logs")
            } else {
                antdMessage.error(response.message || "Failed to delete empty basecaller logs")
            }

            const refreshResponse = await getBasecallerOutputLogs({ runId: Number(runId) })
            if (refreshResponse.code === 1000) {
                setBasecallerOutputStatus('exists')
                setBasecallerOutputFiles(refreshResponse.files || [])
            } else if (refreshResponse.code === 1001) {
                setBasecallerOutputStatus('not-exists')
                setBasecallerOutputFiles([])
            } else {
                setBasecallerOutputStatus('server-off')
                setBasecallerOutputFiles([])
            }
        } catch (err: any) {
            antdMessage.error(err.message || "Failed to delete empty basecaller logs")
        } finally {
            setDeleteEmptyLogsLoading(false)
        }
    }

    const handleSkipReportCheckChange = async (checked: boolean): Promise<void> => {
        if (!runId || skipReportCheckLoading) return

        setSkipReportCheckLoading(true)
        try {
            const response = checked
                ? await setSkipReportCheck({ runId: Number(runId) })
                : await deleteSkipReportCheck({ runId: Number(runId) })

            if (response.code !== 1000) {
                antdMessage.error(response.message || "Failed to update skip report check")
                return
            }

            setSkipReportCheckState(checked)
            antdMessage.success(
                response.message ||
                    (checked ? "skip-report-check.log created" : "skip-report-check.log deleted")
            )
        } catch (err: any) {
            antdMessage.error(err.message || "Failed to update skip report check")
        } finally {
            setSkipReportCheckLoading(false)
        }
    }

    // Fetch instrument reports on mount or when runId changes
    useEffect(() => {
        if (!runId || !isAdmin(userContext.user)) return
        
        let ignore = false
        setReportsLoading(true)
        setReportsError(null)
        
        getRunReports({ runId: Number(runId) })
            .then((data) => {
                if (ignore) return
                if (data.code !== 1000) {
                    setReportsError(data.message || "Failed to fetch reports")
                    setReports([])
                } else {
                    setReports(data.reports || [])
                }
                setReportsLoading(false)
            })
            .catch((e) => {
                if (ignore) return
                setReportsError(e.message || "Failed to fetch reports")
                setReportsLoading(false)
            })
        
        return () => {
            ignore = true
        }
    }, [runId, userContext.user])

    // Load available positions when rbkDevice changes; auto-lock position when flowCellId is known
    useEffect(() => {
        if (!isAdmin(userContext.user)) return
        setRbkPosition('')
        setPositionLockedByFlowcell(false)
        setAvailablePositions([])
        getPositions(rbkDevice).then(async (res) => {
            if (res.code === 0 && res.positions?.length) {
                const names: string[] = res.positions
                    .filter((p: any) => p.state !== 'STATE_HARDWARE_REMOVED')
                    .map((p: any) => p.name as string)
                setAvailablePositions(names)

                if (flowCellId) {
                    // Try to find which position has this flow cell loaded
                    for (const posName of names) {
                        try {
                            const fcResult = await getFlowCellInfo(rbkDevice, posName)
                            if (fcResult.code === 0 && fcResult.data?.flow_cell_id === flowCellId) {
                                setRbkPosition(posName)
                                setPositionLockedByFlowcell(true)
                                return
                            }
                        } catch {}
                    }
                    // Flow cell not found in any position — fall back to normal selection
                    if (names.length === 1) setRbkPosition(names[0])
                } else {
                    if (names.length === 1) setRbkPosition(names[0])
                }
            }
        }).catch(() => {})
    }, [rbkDevice, flowCellId, userContext.user])

    const handleFlowcellCheckProgress = async () => {
        setFlowcellCheckLoading(true)
        setFlowcellCheckError(null)
        setFlowcellCheckData(null)

        try {
            const posResult = await getPositions(rbkDevice)
            if (posResult.code !== 0 || !posResult.positions || posResult.positions.length === 0) {
                throw new Error('No positions available on device')
            }

            const chosenPosition = rbkPosition || posResult.positions[0].name

            // Get protocol run info to check for flowcell check
            const runInfo = await getProtocolRunInfo(rbkDevice, chosenPosition)
            
            // Check if it's a flowcell check protocol
            if (!runInfo.data?.protocol_id || !runInfo.data.protocol_id.includes('flowcell_check')) {
                throw new Error('No flowcell check protocol is currently running or completed on this device')
            }

            setFlowcellCheckData(runInfo.data)
        } catch (err: any) {
            setFlowcellCheckError(err?.message || 'Failed to get flowcell check progress')
            setFlowcellCheckData(null)
        } finally {
            setFlowcellCheckLoading(false)
        }
    }

    const handleGetAquisitionInfo = async () => {
        if (run?.status !== 'running' && run?.status !== 'new') {
            antdMessage.error('Run status is not "running" or "new". This action is only available for active or new runs.')
            return
        }
        setAcquisitionLoading(true)
        setAcquisitionError(null)
        setAcquisitionData(null)

        try {
            const posResult = await getPositions(rbkDevice)
            if (posResult.code !== 0 || !posResult.positions || posResult.positions.length === 0) {
                throw new Error('No positions available')
            }

            const chosenPosition = rbkPosition || posResult.positions[0].name
            const result = await getAcquisitionInfo(rbkDevice, chosenPosition)

            if (result.code === 0) {
                setAcquisitionData(result.data)
                const muxScanResults = result.data?.bream_info?.mux_scan_results
                const readsDirectory = result.data?.reads_directory || result.data?.config_summary?.reads_directory
                if (run?.id && Array.isArray(muxScanResults) && muxScanResults.length > 0) {
                    const runIdFromPath = typeof readsDirectory === 'string'
                        ? (() => {
                            const segments = readsDirectory.split('/').filter((segment) => segment && segment !== '.')
                            const runsIndex = segments.lastIndexOf('runs')
                            if (runsIndex >= 0 && segments[runsIndex + 1]) {
                                return segments[runsIndex + 1]
                            }

                            const dataIndex = segments.lastIndexOf('data')
                            if (dataIndex >= 0 && segments[dataIndex + 1]) {
                                return segments[dataIndex + 1]
                            }

                            return null
                        })()
                        : null

                    if (runIdFromPath && String(run.id) === String(runIdFromPath)) {
                        const serializedMuxScanResults = JSON.stringify(muxScanResults)
                        const saveResult = await updateRun({
                            runId: run.id,
                            mux_scan_results: serializedMuxScanResults,
                        })
                        if (saveResult.code === 1000) {
                            setRun(prev => prev ? { ...prev, mux_scan_results: serializedMuxScanResults } : prev)
                        } else {
                            antdMessage.warning(saveResult.message || 'Failed to save mux scan results')
                        }
                    } else {
                        antdMessage.warning(
                            `Mux scan results not saved: reads_directory run ID (${runIdFromPath || 'unknown'}) does not match current run ID (${run.id})`
                        )
                    }
                }
            } else {
                setAcquisitionError(result.message || 'Failed to get acquisition info')
            }
        } catch (err: any) {
            setAcquisitionError(err.message || 'Failed to get acquisition info')
        } finally {
            setAcquisitionLoading(false)
        }
    }

    const handleGetChannelStates = async () => {
        if (run?.status !== 'running' && run?.status !== 'new') {
            antdMessage.error('Run status is not "running" or "new". This action is only available for active or new runs.')
            return
        }
        setChannelStatesLoading(true)
        setChannelStatesError(null)
        setChannelStatesData(null)

        try {
            const posResult = await getPositions(rbkDevice)
            if (posResult.code !== 0 || !posResult.positions || posResult.positions.length === 0) {
                throw new Error('No positions available')
            }

            const chosenPositions = rbkPosition
                ? posResult.positions.filter((p: any) => p.name === rbkPosition)
                : posResult.positions

            const allChannelStates: any[] = []
            for (const pos of chosenPositions) {
                const result = await getChannelStates(rbkDevice, pos.name)
                if (result.code === 0) {
                    allChannelStates.push({ ...result.data, positionName: pos.name })
                }
            }

            if (allChannelStates.length > 0) {
                setChannelStatesData(allChannelStates)
                if (run?.id) {
                    const withTimestamp = allChannelStates.map(cs => ({ ...cs, savedAt: new Date().toISOString() }))
                    const saveResult = await updateRun({
                        runId: run.id,
                        channel_states: JSON.stringify(withTimestamp),
                    })
                    if (saveResult.code === 1000) {
                        setRun(prev => prev ? { ...prev, channel_states: JSON.stringify(withTimestamp) } : prev)
                    } else {
                        antdMessage.warning(saveResult.message || 'Channel states fetched but failed to save to database')
                    }
                }
            }
        } catch (err: any) {
            setChannelStatesError(err.message || 'Failed to fetch channel states')
        } finally {
            setChannelStatesLoading(false)
        }
    }

    const normalizeReportChannelStates = (reportChannelStates: any): any[] => {
        if (Array.isArray(reportChannelStates)) {
            return reportChannelStates
        }
        if (!reportChannelStates || typeof reportChannelStates !== 'object') {
            return []
        }

        const entries = Object.entries(reportChannelStates)
        const maxLen = entries.reduce((max, [, val]: any) => {
            const times = val?.state_times
            return Array.isArray(times) ? Math.max(max, times.length) : max
        }, 0)

        const flattened: any[] = []
        for (let idx = 0; idx < maxLen; idx++) {
            let bestState = 'unclassified'
            let bestValue = -1

            for (const [stateName, stateObj] of entries) {
                const typedStateObj: any = stateObj
                const raw = typedStateObj?.state_times?.[idx] ?? 0
                const numeric = Number(raw)
                const value = Number.isFinite(numeric) ? numeric : 0
                if (value > bestValue) {
                    bestValue = value
                    bestState = stateName
                }
            }

            flattened.push({
                channel: idx + 1,
                state_name: bestState,
            })
        }

        return flattened
    }

    const handleGetMuxScansFromReport = async () => {
        if (!run?.id) {
            antdMessage.error('Run ID not available')
            return
        }

        setReportMuxChannelLoading(true)
        setAcquisitionError(null)

        try {
            const response = await getReportMuxScansFromReport({ runId: run.id })
            if (response.code !== 1000 || !response.data) {
                throw new Error(response.message || 'Failed to fetch report data from GPU backend')
            }

            const muxScanResults = Array.isArray(response.data.mux_scan_results) ? response.data.mux_scan_results : []

            if (muxScanResults.length > 0) {
                const acquisitionFromReport = {
                    bream_info: { mux_scan_results: muxScanResults },
                    source: 'gpu-report',
                }
                setAcquisitionData(acquisitionFromReport)
                const serializedMux = JSON.stringify(muxScanResults)
                const saveResult = await updateRun({ runId: run.id, mux_scan_results: serializedMux })
                if (saveResult.code === 1000) {
                    setRun(prev => prev ? { ...prev, mux_scan_results: serializedMux } : prev)
                } else {
                    antdMessage.warning(saveResult.message || 'Failed to save mux scan results')
                }
            }

            if (muxScanResults.length === 0) {
                antdMessage.warning('Report retrieved but no mux scan results were found')
            } else {
                antdMessage.success('Loaded mux scan results from report')
            }
        } catch (err: any) {
            const msg = err?.message || 'Failed to fetch report mux scan results'
            setAcquisitionError(msg)
        } finally {
            setReportMuxChannelLoading(false)
        }
    }

    const handleStopProtocol = async () => {
        if (run?.status !== 'running' && run?.status !== 'new') {
            antdMessage.error('Run status is not "running" or "new". This action is only available for active or new runs.')
            return
        }

        modal.confirm({
            title: 'Stop Protocol',
            content: `Are you sure you want to stop the protocol for run ${run.id}? This action cannot be undone.`,
            okText: 'Stop Protocol',
            okButtonProps: { danger: true },
            onOk: async () => {
                setStopProtocolLoading(true)
                const hide = antdMessage.loading('Stopping protocol...', 0)
                try {
                    const posResult = await getPositions(rbkDevice)
                    if (posResult.code !== 0 || !posResult.positions || posResult.positions.length === 0) {
                        throw new Error('No positions available on device')
                    }

                    const chosenPosition = rbkPosition || posResult.positions[0].name

                    // Get protocol run info to verify it belongs to this run
                    const runInfo = await getProtocolRunInfo(rbkDevice, chosenPosition)
                    if (runInfo.code !== 0 || !runInfo.data) {
                        throw new Error('Failed to get protocol run info from device')
                    }

                    const deviceRunId = runInfo.data.experiment_group_id
                    if (deviceRunId && String(deviceRunId) !== String(run.id)) {
                        throw new Error(`Protocol on device belongs to run "${deviceRunId}", not the current run (${run.id}). Aborting to prevent stopping the wrong run.`)
                    }

                    const protocolRunId = runInfo.data?.run_id
                    const result = await stopProtocol(rbkDevice, chosenPosition, protocolRunId)
                    hide()
                    if (result.code === 0) {
                        antdMessage.success('Protocol stopped successfully')
                    } else {
                        throw new Error(result.message || 'Failed to stop protocol')
                    }
                } catch (err: any) {
                    hide()
                    antdMessage.error(err.message || 'Failed to stop protocol')
                } finally {
                    setStopProtocolLoading(false)
                }
            }
        })
    }

    const handleUpdateRunUntil = async () => {
        if (run?.status !== 'running' && run?.status !== 'new') {
            antdMessage.error('Run status is not "running" or "new". This action is only available for active or new runs.')
            return
        }
        const hours = runUntilHours
        
        if (!hours || hours <= 0) {
            antdMessage.error('Please enter a valid number of hours (e.g., 3, 3.5, 4)')
            return
        }

        const hide = antdMessage.loading('Updating run-until criteria...', 0)
        try {
            const posResult = await getPositions(rbkDevice)
            if (posResult.code !== 0) {
                const errorMsg = posResult.message || 'Failed to connect to device'
                throw new Error(`Failed to get positions from ${rbkDevice}: ${errorMsg}. Are you sure you are using ${rbkDevice}?`)
            }
            
            if (!posResult.positions || posResult.positions.length === 0) {
                throw new Error(`No flow cell positions available on ${rbkDevice}. Please check if the device is properly connected and has flow cells installed.`)
            }
            
            const chosenPosition = rbkPosition || posResult.positions[0].name
            const seconds = Math.floor(hours * 3600)

            const result = await updateRunUntil(rbkDevice, chosenPosition, seconds)
            
            if (result.code !== 0) {
                throw new Error(result.message || 'Failed to update run-until criteria')
            }
            
            // Update duration in database
            if (run?.id) {
                try {
                    await updateRun({ runId: run.id, duration: hours })
                    // Update local state to reflect the change in UI
                    setDuration(hours)
                } catch (updateErr) {
                    console.error('Failed to update run duration:', updateErr)
                    // Don't fail the whole operation if update fails
                }
            }
            
            hide()
            antdMessage.success(`Run-until criteria updated to ${hours} hours`)
            setRunUntilHours(null)
        } catch (err: any) {
            hide()
            antdMessage.error(err.message || 'Failed to update run-until criteria')
        }
    }

    const handleStartRbkRun = async () => {
        if (run?.status !== 'new') {
            antdMessage.error('Run status is not "new". This action is only available for new runs.')
            return
        }
        const hours = rbkHours
        
        if (!run?.id) {
            antdMessage.error('Run ID not available')
            return
        }
        
        if (!hours || hours <= 0) {
            antdMessage.error('Please enter a valid number of hours (e.g., 3, 3.5, 4)')
            return
        }

        // Check flow cell match BEFORE showing confirmation
        const checkHide = antdMessage.loading('Checking flow cell...', 0)
        try {
            const posResult = await getPositions(rbkDevice)
            if (posResult.code !== 0 || !posResult.positions || posResult.positions.length === 0) {
                checkHide()
                antdMessage.error('No positions available on device')
                return
            }
            
            const chosenPosition = rbkPosition || posResult.positions[0].name

            const flowcellResult = await getFlowCellInfo(rbkDevice, chosenPosition)
            if (flowcellResult.code !== 0 || !flowcellResult.data?.flow_cell_id) {
                checkHide()
                antdMessage.error('No flow cell detected in device position')
                return
            }

            // Check if flowcell matches the run's flowcell
            const deviceFlowcellId = flowcellResult.data.flow_cell_id
            const runFlowcellId = run.flowCellId
            if (deviceFlowcellId !== runFlowcellId) {
                checkHide()
                setFlowcellCheckError(`Flow cell mismatch: Run expects "${runFlowcellId}" but device has "${deviceFlowcellId}"`)
                return
            }

            const protocolResult = await getProtocolRunInfo(rbkDevice, chosenPosition)
            const protocolState = protocolResult.data?.state ?? protocolResult.data?.protocol_state
            if (protocolState === 'PROTOCOL_RUNNING') {
                checkHide()
                antdMessage.error('Cannot start run: A protocol is already running on this flow cell. ')
                return
            }

            checkHide()

            // All checks passed, show confirmation modal
            modal.confirm({
                title: 'Start RBK Sequencing Run',
                content: (
                    <>
                        <p><strong>Device:</strong> {rbkDevice} / {chosenPosition}</p>
                        <p><strong>Run ID:</strong> {run.id}</p>
                        <p><strong>Flow Cell:</strong> {deviceFlowcellId}</p>
                        <p><strong>Duration:</strong> {hours} hours</p>
                        <p><strong>Output:</strong> /data/plab/runs</p>
                    </>
                ),
                onOk: async () => {
                    setRbkRunning(true)
                    setRbkError(null)
                    const hide = antdMessage.loading('Starting RBK run...', 0)
                    try {
                        const protocolParams: any = {
                            identifier: 'sequencing/sequencing_MIN114_DNA_e8_2_400K:FLO-MIN114:SQK-RBK114-96:400',
                            experiment_group_id: String(run.id),
                            disable_active_channel_selection: false,
                            mux_scan_period: 1.5,
                            target_run_until_criteria: {
                                stop_criteria: {
                                    criteria: {
                                        runtime: {
                                            "@type": "type.googleapis.com/google.protobuf.UInt64Value",
                                            "value": String(Math.floor(hours * 3600))
                                        }
                                    }
                                }
                            },
                            writer_configuration: {
                                read_pod5_config: {
                                    reads_per_file: 5000
                                }
                            },
                            args: [
                                `--pod5=on`,
                                `--fast5=off`
                            ]
                        }

                        // Only add offload_location_info for non-outpond/non-sierra devices
                        if (rbkDevice !== 'outpond' && rbkDevice !== 'sierra') {
                            protocolParams.offload_location_info = {
                                offload_location_path: '/data/plab/runs'
                            }
                        }

                        const result = await startProtocol(rbkDevice!, chosenPosition, protocolParams)
                        
                        if (result.code !== 0) throw new Error(result.message || 'Failed to start RBK run')
                        
                        // Update run status, device, and duration in database
                        try {
                            await updateRun({ runId: run.id, status: 'running', device: rbkDevice, duration: hours })
                            // Update local state to reflect the change in UI
                            setDevice(rbkDevice)
                            setDuration(hours)
                        } catch (updateErr) {
                            console.error('Failed to update run device and duration:', updateErr)
                            // Don't fail the whole operation if update fails
                        }
                        
                        // Regenerate run settings after starting the run
                        try {
                            await regenerateRunSettings({ runId: run.id })
                            console.log('Run settings regenerated successfully')
                        } catch (settingsErr) {
                            console.error('Failed to regenerate run settings:', settingsErr)
                            // Don't fail the whole operation if settings regeneration fails
                        }
                        
                        hide()
                        antdMessage.success('RBK sequencing run started successfully')
                        setRbkHours(null)
                    } catch (err: any) {
                        hide()
                        setRbkError(err.message || 'Failed to start RBK run')
                    } finally {
                        setRbkRunning(false)
                    }
                }
            })
        } catch (err: any) {
            checkHide()
            antdMessage.error(err.message || 'Failed to check flow cell')
        }
    }

    const handleSyncData = async () => {
        if (!run?.id) {
            antdMessage.error('Run ID not available')
            return
        }

        const deviceSourcePath = (device === 'outpond' || device === 'sierra')
            ? `/Library/MinKNOW/data/${run.id}/`
            : `/data/plab/runs/${run.id}/`

        modal.confirm({
            title: `Sync Data from ${device}`,
            content: (
                <>
                    <p>This will sync data from {device} device to local storage.</p>
                    <p><strong>Run ID:</strong> {run.id}</p>
                    <p><strong>Command:</strong> rsync -av {device}:{deviceSourcePath} /data/plab/runs/{run.id}/</p>
                </>
            ),
            onOk: async () => {
                setSyncLoading(true)
                const hide = antdMessage.loading(`Starting data sync from ${device}...`, 0)
                try {
                    const response = await fetch(`/api/syncRunData`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ runId: run.id, from: device, deviceSourcePath })
                    })
                    
                    const result = await response.json()
                    
                    if (!response.ok || result.code !== 1000) {
                        throw new Error(result.message || 'Failed to start sync')
                    }
                    
                    hide()
                    antdMessage.success('Data sync started successfully. The process is running in the background.')
                } catch (err: any) {
                    hide()
                    antdMessage.error(err.message || 'Failed to start sync')
                } finally {
                    setSyncLoading(false)
                }
            }
        })
    }

    const handleSummarize = async (fileName: string) => {
        setSummarizeModalFileName(fileName)
        setSummarizeModalOpen(true)
        setSummarizeModalLoading(true)
        setSummarizeModalError(null)
        setSummarizeModalData(null)

        try {
            const response = await fetch(
                `/api/download-basecaller-output-log/${run?.id}?fileName=${encodeURIComponent(fileName)}`
            )
            if (!response.ok) {
                throw new Error(`Failed to fetch log: ${response.status} ${response.statusText}`)
            }
            const text = await response.text()

            // Extract all "Simplex reads basecalled: N" values (preceded by "> ")
            const basecalledMatches = [...text.matchAll(/Simplex reads basecalled:\s*(\d[\d,]*)/g)]
            const simplexBasecalled = basecalledMatches.map(m => parseInt(m[1].replace(/,/g, ''), 10))

            // Extract all "Simplex reads filtered: N" values
            const filteredMatches = [...text.matchAll(/Simplex reads filtered:\s*(\d[\d,]*)/g)]
            const simplexFiltered = filteredMatches.map(m => parseInt(m[1].replace(/,/g, ''), 10))

            // Extract barcode counts — format: "SQK-RBK114-96_barcodeNN : 97" (spaces around colon)
            // Sum across all occurrences (multiple pod5 files in one log)
            const barcodeMatches = [...text.matchAll(/(SQK-RBK114-96_barcode\d+)\s*:\s*(\d+)/g)]
            const barcodeSums: Record<string, number> = {}
            for (const m of barcodeMatches) {
                barcodeSums[m[1]] = (barcodeSums[m[1]] ?? 0) + parseInt(m[2], 10)
            }
            const barcodeCounts: { name: string; count: number }[] = Object.entries(barcodeSums)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
            const barcodeTotal = barcodeCounts.reduce((sum, b) => sum + b.count, 0)

            // Extract per-occurrence unclassified counts — format: "unclassified : 1546"
            const unclassifiedMatches = [...text.matchAll(/unclassified\s*:\s*(\d+)/g)]
            const simplexUnclassified = unclassifiedMatches.map(m => parseInt(m[1], 10))

            setSummarizeModalData({ simplexBasecalled, simplexFiltered, simplexUnclassified, barcodeTotal, barcodeCounts })
        } catch (err: any) {
            setSummarizeModalError(err.message || 'Failed to load log file')
        } finally {
            setSummarizeModalLoading(false)
        }
    }

    const handleCleanRunFolder = () => {
        if (!run?.id) return

        modal.confirm({
            title: 'Clean Run Folder',
            content: `This will delete the work and peekpath/work folders and the contents of every subfolder under no_sample_id in the run folder for run ${run.id}. This cannot be undone. Continue?`,
            okText: 'Clean',
            okButtonProps: { danger: true, loading: cleanRunFolderLoading },
            onOk: async () => {
                setCleanRunFolderLoading(true)
                try {
                    const res = await cleanRunFolder({ runId: run.id })
                    if (res.code !== 1000) {
                        throw new Error(res.message || 'Failed to clean run folder')
                    }
                    antdMessage.success(res.message || 'Run folder cleaned')
                } catch (err: any) {
                    antdMessage.error(err.message || 'Failed to clean run folder')
                    throw err
                } finally {
                    setCleanRunFolderLoading(false)
                }
            }
        })
    }

    const handleResetRunningLog = () => {
        if (!run?.id) return

        let password = ''

        modal.confirm({
            title: 'Reset running.log',
            content: (
                <div>
                    <div style={{ marginBottom: 12 }}>
                        Enter the password to delete running.log for run {run.id}.
                    </div>
                    <Input.Password
                        placeholder="Password"
                        onChange={(e) => {
                            password = e.target.value
                        }}
                        onPressEnter={() => undefined}
                    />
                </div>
            ),
            okText: 'Reset',
            okButtonProps: { danger: true, loading: resetRunningLogLoading },
            onOk: async () => {
                if (!password.trim()) {
                    throw new Error('Password is required')
                }

                setResetRunningLogLoading(true)
                try {
                    const result = await resetRunningLog({ runId: run.id, password: password.trim() })
                    if (result.code !== 1000) {
                        throw new Error(result.message || 'Failed to reset running.log')
                    }

                    antdMessage.success(result.message || 'running.log deleted successfully')
                    setNextflowStatus('not running')
                } catch (err: any) {
                    antdMessage.error(err.message || 'Failed to reset running.log')
                    throw err
                } finally {
                    setResetRunningLogLoading(false)
                }
            }
        })
    }

    if (loading) return <Skeleton />
    if (error) return <div>Error: {error}</div>
    if (!run) return <div>No run found with ID {runId}</div>
    // console.log("Run details:", run)
    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <Button type="link" style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
                {"<< Back"}
            </Button>
            <h2 style={{ marginBottom: 8 }}>Run {run.id}</h2>
            <Button
                size="small"
                style={{ marginBottom: 24 }}
                onClick={handleCleanRunFolder}
                loading={cleanRunFolderLoading}
            >
                Clean Run Folder
            </Button>

            {showSettings && (
                <div style={{ marginBottom: 24 }}>
                    <b>Run Settings:</b>
                    {settingsLoading ? (
                        <div>Loading...</div>
                    ) : settingsError ? (
                        <div style={{ color: "red" }}>{settingsError}</div>
                    ) : null}
                </div>
            )}

            <NameValueRow
                Name="Nextflow Log"
                Value={
                    nextflowLogStatus === 'checking' ? (
                        <span style={{ color: "#888" }}>Checking...</span>
                    ) : nextflowLogStatus === 'server-off' ? (
                        <span style={{ color: "#ff4d4f" }}>Server not on</span>
                    ) : nextflowLogStatus === 'not-exists' ? (
                        <span style={{ color: "#888" }}>Empty</span>
                    ) : (
                        <a
                            href={`/api/download-nextflow-log/${run.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1890ff" }}
                        >
                            View
                        </a>
                    )
                }
            />

            <NameValueRow
                Name="Watch Start Log"
                Value={
                    nextflowLogStatus === 'checking' ? (
                        <span style={{ color: "#888" }}>Checking...</span>
                    ) : nextflowLogStatus === 'server-off' ? (
                        <span style={{ color: "#ff4d4f" }}>Server not on</span>
                    ) : nextflowLogStatus === 'not-exists' ? (
                        <span style={{ color: "#888" }}>Empty</span>
                    ) : (
                        <a
                            href={`/api/download-watchtoStart-log/${run.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1890ff" }}
                        >
                            View
                        </a>
                    )
                }
            />

            <NameValueRow
                Name="Summary process log"
                Value={
                    nextflowLogStatus === 'checking' ? (
                        <span style={{ color: "#888" }}>Checking...</span>
                    ) : nextflowLogStatus === 'server-off' ? (
                        <span style={{ color: "#ff4d4f" }}>Server not on</span>
                    ) : nextflowLogStatus === 'not-exists' ? (
                        <span style={{ color: "#888" }}>Empty</span>
                    ) : (
                        <a
                            href={`/api/download-summaryProcess-log/${run.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1890ff" }}
                        >
                            View
                        </a>
                    )
                }
            />

            <NameValueRow
                Name="Dorado Process Log"
                Value={
                    nextflowLogStatus === 'checking' ? (
                        <span style={{ color: "#888" }}>Checking...</span>
                    ) : nextflowLogStatus === 'server-off' ? (
                        <span style={{ color: "#ff4d4f" }}>Server not on</span>
                    ) : nextflowLogStatus === 'not-exists' ? (
                        <span style={{ color: "#888" }}>Empty</span>
                    ) : (
                        <a
                            href={`/api/download-doradoProcess-log/${run.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1890ff" }}
                        >
                            View
                        </a>
                    )
                }
            />

            <NameValueRow
                Name="Basecaller output"
                Value={
                    basecallerOutputStatus === 'checking' ? (
                        <span style={{ color: "#888" }}>Checking...</span>
                    ) : basecallerOutputStatus === 'server-off' ? (
                        <span style={{ color: "#ff4d4f" }}>Server not on</span>
                    ) : basecallerOutputStatus === 'not-exists' ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: "#888" }}>Empty</span>
                            <Button size="small" onClick={handleDeleteEmptyLogs} loading={deleteEmptyLogsLoading}>
                                Delete Empty Logs
                            </Button>
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <Button size="small" onClick={handleDeleteEmptyLogs} loading={deleteEmptyLogsLoading} style={{ width: "fit-content" }}>
                                Delete Empty Logs
                            </Button>
                            {basecallerOutputFiles.map((fileName) => (
                                <div key={fileName} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <a
                                        href={`/api/download-basecaller-output-log/${run.id}?fileName=${encodeURIComponent(fileName)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#1890ff" }}
                                    >
                                        {fileName}
                                    </a>
                                    <Button size="small" onClick={() => handleSummarize(fileName)}>
                                        Summarize
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )
                }
            />

            <NameValueRow
                Name="Mux Scan Results"
                Value={(() => {
                    const rawMux = run.mux_scan_results
                    if (!rawMux || rawMux.trim() === "") {
                        return <span style={{ color: "#888" }}>Empty</span>
                    }

                    try {
                        const parsed = JSON.parse(rawMux)
                        if (!Array.isArray(parsed) || parsed.length === 0) {
                            return <span style={{ color: "#888" }}>Empty</span>
                        }

                        return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {parsed.map((result: any, idx: number) => (
                                    <Tag key={idx} color="green" style={{ margin: 0 }}>
                                        {result?.counts?.single_pore ?? 'N/A'}
                                    </Tag>
                                ))}
                            </div>
                        )
                    } catch {
                        return <span style={{ color: "#888" }}>Invalid format</span>
                    }
                })()}
            />

            <NameValueRow
                Name="Error Log"
                Value={
                    errorLogStatus === 'checking' ? (
                        <span style={{ color: "#888" }}>Checking...</span>
                    ) : errorLogStatus === 'server-off' ? (
                        <span style={{ color: "#ff4d4f" }}>Server not on</span>
                    ) : errorLogStatus === 'not-exists' ? (
                        <span style={{ color: "#888" }}>Empty</span>
                    ) : (
                        <a 
                            href={`/api/download-run-error-log/${run.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1890ff" }}
                        >
                            Download error.log
                        </a>
                    )
                }
            />

            {isAdmin(userContext.user) && (
                <NameValueRow
                    Name="Used"
                    Value={
                        <Checkbox
                            checked={used}
                            onChange={(e) => {
                                const newValue = e.target.checked
                                setUsed(newValue)
                                handleFieldSave({ used: newValue })
                            }}
                        >
                            used
                        </Checkbox>
                    }
                />
            )}

            <NameValueRow
                Name="Nextflow Status"
                Value={
                    nextflowStatusLoading ? (
                        <span style={{ color: "#888" }}>Loading...</span>
                    ) : (
                        <Space wrap>
                            <span
                                style={{
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    backgroundColor: nextflowStatus === "running" ? "#52c41a" : "#d9d9d9",
                                    color: nextflowStatus === "running" ? "white" : "#666",
                                    fontWeight: "500"
                                }}
                            >
                                {nextflowStatus}
                            </span>
                            {nextflowStatus === "running" && canResetRunningLog && (
                                <Button
                                    danger
                                    size="small"
                                    onClick={handleResetRunningLog}
                                    loading={resetRunningLogLoading}
                                >
                                    Reset
                                </Button>
                            )}
                            <Checkbox
                                checked={skipReportCheck}
                                onChange={(e) => handleSkipReportCheckChange(e.target.checked)}
                                disabled={skipReportCheckLoading}
                            >
                                skip checking report html
                            </Checkbox>
                        </Space>
                    )
                }
            />
            <NameValueRow
                Name="Status"
                Value={
                    <Dropdown
                        menu={{
                            items: RUN_STATUS_OPTIONS.map((opt) => ({
                                key: opt.value,
                                label: opt.label,
                            })),
                            onClick: ({ key }) => {
                                setStatus(key as RunStatusType)
                                if (key !== run.status) {
                                    handleFieldSave({ status: key as RunStatusType })
                                }
                            },
                            selectedKeys: [status || run.status],
                        }}
                        trigger={["click"]}
                    >
                        <Button
                            style={
                                (status || run.status) === "new"
                                    ? { color: "black", backgroundColor: "LightGreen" }
                                    : (status || run.status) === "running"
                                    ? { color: "black", backgroundColor: "YellowGreen" }
                                    : (status || run.status) === "completed"
                                    ? { color: "white", backgroundColor: "green" }
                                    : (status || run.status) === "canceled"
                                    ? { color: "black", backgroundColor: "OldLace" }
                                    : undefined
                            }
                        >
                            {RUN_STATUS_OPTIONS.find((opt) => opt.value === (status || run.status))?.label ||
                                run.status}
                        </Button>
                    </Dropdown>
                }
            />
            <NameValueRow Name="Type" Value={run.type} />
            <NameValueRow
                Name="Plates"
                Value={
                    Array.isArray(run.plateIds) && run.plateIds.length > 0 ? (
                        <span>
                            {run.plateIds.map((pid: number, idx: number) => (
                                <React.Fragment key={pid}>
                                    <Link to={`/admin/plate/${pid}`}>{pid}</Link>
                                    {idx < (run.plateIds?.length ?? 0) - 1 ? ", " : ""}
                                </React.Fragment>
                            ))}
                        </span>
                    ) : (
                        <span style={{ color: "#bbb" }}>None</span>
                    )
                }
            />
            <NameValueRow
                Name="Orders"
                Value={
                    Array.isArray(run.orderIds) && run.orderIds.length > 0 ? (
                        <span>
                            {run.orderIds.map((oid: number, idx: number) => (
                                <React.Fragment key={oid}>
                                    <Link to={`/admin/order/${oid}`}>{oid}</Link>
                                    {idx < (run.orderIds?.length ?? 0) - 1 ? ", " : ""}
                                </React.Fragment>
                            ))}
                        </span>
                    ) : (
                        <span style={{ color: "#bbb" }}>None</span>
                    )
                }
            />
            <NameValueRow
                Name="Total Samples"
                Value={
                    run.nSamples != null ? (
                        <span>{run.nSamples}</span>
                    ) : (
                        <span style={{ color: "#bbb" }}>—</span>
                    )
                }
            />
            <NameValueRow Name="New Cell">
                <Checkbox
                    checked={newCell}
                    onChange={(e) => {
                        const newValue = e.target.checked
                        setNewCell(newValue)
                        handleFieldSave({ new_cell: newValue })
                    }}
                >
                    New flow cell used
                </Checkbox>
            </NameValueRow>
            <NameValueRow Name="Device">
                <Space>
                    <Select
                        style={{ width: 180 }}
                        value={device}
                        onChange={(value) => {
                            setDevice(value)
                            handleFieldSave({ device: value })
                        }}
                        options={[
                            { value: 'a6000', label: 'a6000' },
                            { value: 'mk1c', label: 'mk1c' },
                            { value: 'outpond', label: 'outpond' },
                            { value: 'sierra', label: 'Sierra' }
                        ]}
                    />
                    {device !== 'a6000' && (
                        <Button
                            type="default"
                            onClick={handleSyncData}
                            loading={syncLoading}
                        >
                            Sync Data
                        </Button>
                    )}
                </Space>
            </NameValueRow>       
            <NameValueRow Name="Flow Cell Name">
                <Space>
                    <Input
                        style={{ width: 180 }}
                        value={flowCellId}
                        placeholder="Flow Cell Name"
                        onChange={(e) => setFlowCellId(e.target.value)}
                        onBlur={() => {
                            if (flowCellId !== (run.flowCellId || "")) {
                                handleFieldSave({ flowCellId })
                            }
                        }}
                        onPressEnter={(e) => {
                            ;(e.target as HTMLInputElement).blur()
                        }}
                    />
                    <span style={{ color: '#888' }}>ID:</span>
                    <InputNumber
                        style={{ width: 100 }}
                        value={flowCellNumericId}
                        placeholder="Flow Cell ID"
                        min={1}
                        precision={0}
                        onChange={(value) => {
                            if (value === null || value === undefined) {
                                setFlowCellNumericId(undefined)
                                return
                            }
                            setFlowCellNumericId(value)
                            // Find the flow cell with this ID and update the name
                            const flowCell = flowCells.find(fc => fc.id === value)
                            if (flowCell && flowCell.name !== flowCellId) {
                                setFlowCellId(flowCell.name)
                                if (flowCell.name !== (run.flowCellId || "")) {
                                    handleFieldSave({ flowCellId: flowCell.name })
                                }
                            }
                        }}
                        onPressEnter={(e) => {
                            ;(e.target as HTMLInputElement).blur()
                        }}
                        disabled={flowCellsLoading}
                    />
                </Space>
            </NameValueRow>
            <NameValueRow Name="Pores left after run">
                <InputNumber
                    style={{ width: 120 }}
                    value={pores === null ? undefined : pores}
                    placeholder="Pores"
                    min={0}
                    onChange={(value) => setPores(value === null ? undefined : value)}
                    onBlur={() => {
                        if (pores !== run.pores) {
                            handleFieldSave({ pores: pores === null ? undefined : pores })
                        }
                    }}
                    onPressEnter={(e) => {
                        ;(e.target as HTMLInputElement).blur()
                    }}
                />
            </NameValueRow>
            <NameValueRow Name="Duration (hours)">
                <InputNumber
                    style={{ width: 120 }}
                    value={duration === null ? undefined : duration}
                    placeholder="Duration"
                    min={0}
                    step={0.1}
                    onChange={(value) => setDuration(value === null ? undefined : value)}
                    onBlur={() => {
                        if (duration !== run.duration) {
                            handleFieldSave({ duration: duration === null ? undefined : duration })
                        }
                    }}
                    onPressEnter={(e) => {
                        ;(e.target as HTMLInputElement).blur()
                    }}
                />
            </NameValueRow>
            <NameValueRow Name="Cell Died">
                <Checkbox
                    checked={cellDied}
                    onChange={(e) => {
                        const newValue = e.target.checked
                        setCellDied(newValue)
                        handleFieldSave({ cell_died: newValue })
                    }}
                >
                    Flow cell died after run
                </Checkbox>
            </NameValueRow>     
            <NameValueRow Name="Reads in reads.all.bam" Value={run.nreads || "N/A"} />
            <NameValueRow Name="Notes">
                <Input.TextArea
                    style={{ width: 500 }}
                    value={notes}
                    placeholder="Notes"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => {
                        if ((notes ?? "") !== (run.notes ?? "")) {
                            handleFieldSave({ notes })
                        }
                    }}
                    onPressEnter={(e) => {
                        ;(e.target as HTMLTextAreaElement).blur()
                    }}
                />
            </NameValueRow>
            <NameValueRow Name="Created" Value={new Date(run.ts).toLocaleString()} />
            <NameValueRow Name="Last Modified" Value={run.lastSave ? new Date(run.lastSave).toLocaleString() : "Never"} />
            <NameValueRow 
                Name="MinKnow Report" 
                Value={
                    <Button 
                        type="link" 
                        size="small"
                        style={{ padding: 0, height: 'auto' }}
                        onClick={() => window.open(`/api/download-run-report/${run.id}`, "_blank")}
                    >
                        html
                    </Button>
                } 
            />
            {isAdmin(userContext.user) && (
                <NameValueRow
                    Name="Nextflow Report"
                    Value={
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={() => window.open(`/api/download-nextflow-report/${run.id}`, "_blank")}
                        >
                            html
                        </Button>
                    }
                />
            )}
            {isAdmin(userContext.user) && (
                <NameValueRow
                    Name="Make Peek"
                    Value={
                        <Button
                            size="small"
                            loading={makePeekLoading}
                            onClick={async () => {
                                setMakePeekLoading(true)
                                try {
                                    const result = await makePeek({ runId: run.id })
                                    if (result.code === 0) {
                                        antdMessage.success(result.message || 'Peek analysis started successfully')
                                    } else {
                                        antdMessage.error(result.message || 'Failed to start peek analysis')
                                    }
                                } catch (err: any) {
                                    antdMessage.error(err.message || 'Failed to start peek analysis')
                                } finally {
                                    setMakePeekLoading(false)
                                }
                            }}
                        >
                            Make Peek
                        </Button>
                    }
                />
            )}
            {isAdmin(userContext.user) && (
                <NameValueRow
                    Name="Show Peek Result"
                    Value={
                        <Space>
                            <Button
                                size="small"
                                loading={peekResultLoading}
                                onClick={async () => {
                                    setPeekResultLoading(true)
                                    setPeekResultMessage(null)
                                    setPeekResultOrders(null)
                                    setPeekResultTotalReads(null)
                                    setPeekErrorLogExists(false)
                                    try {
                                        const [result, errorLogResult] = await Promise.all([
                                            showPeekResult({ runId: run.id }),
                                            showPeekErrorLog({ runId: run.id }),
                                        ])
                                        const hasPeekErrorLog = errorLogResult.code === 1000 && errorLogResult.exists === true
                                        setPeekErrorLogExists(hasPeekErrorLog)
                                        if (result.code === 1000 || result.code === 0) {
                                            setPeekResultOrders(result.orders || [])
                                            setPeekResultTotalReads(result.total_reads_in_bam ?? null)
                                        } else if (hasPeekErrorLog) {
                                            setPeekResultOrders([])
                                            setPeekResultMessage(result.message || 'Peek failed. error.log is available below.')
                                        } else {
                                            setPeekResultMessage(result.message || 'No peek results found')
                                        }
                                    } catch (err: any) {
                                        setPeekResultMessage(err.message || 'Failed to get peek results')
                                    } finally {
                                        setPeekResultLoading(false)
                                    }
                                }}
                            >
                                Show Peek Result
                            </Button>
                            <Button
                                size="small"
                                href={`/api/download-peek-nextflowlog/${run.id}`}
                                target="_blank"
                            >
                                Peek Nextflow Log
                            </Button>
                        </Space>
                    }
                />
            )}
            {isAdmin(userContext.user) && peekResultMessage && (
                <div style={{ marginTop: 4, marginBottom: 8, color: '#ff4d4f', fontSize: '0.9em' }}>
                    {peekResultMessage}
                </div>
            )}
            {isAdmin(userContext.user) && (peekResultOrders !== null || peekErrorLogExists) && (
                <Card title="Peek Results" size="small" style={{ marginTop: 8, marginBottom: 8 }}>
                    {peekResultTotalReads !== null && (
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Total reads in BAM: {peekResultTotalReads.toLocaleString()}
                        </div>
                    )}
                    {peekErrorLogExists && (
                        <div style={{ marginBottom: 8 }}>
                            <Button
                                type="link"
                                size="small"
                                href={`/api/download-peek-error/${run.id}`}
                                target="_blank"
                                style={{ padding: 0 }}
                            >
                                error.log
                            </Button>
                        </div>
                    )}
                    {(peekResultOrders ?? []).length === 0 ? (
                        <p style={{ margin: 0, color: '#999' }}>No orders found under peekpath/orders.</p>
                    ) : (
                        (peekResultOrders ?? []).map(order => (
                            <Card key={order.orderId} size="small" title={`Order ${order.orderId}`} style={{ marginBottom: 8 }}>
                                <Space wrap>
                                    {order.files.resultHtml && (
                                        <Button
                                            type="link"
                                            size="small"
                                            href={`/api/download-peek-result/${run.id}/${order.orderId}/result.html`}
                                            target="_blank"
                                        >
                                            result.html
                                        </Button>
                                    )}
                                    {order.files.zip && (
                                        <Button
                                            type="link"
                                            size="small"
                                            href={`/api/download-peek-result/${run.id}/${order.orderId}/${order.files.zip}`}
                                            target="_blank"
                                        >
                                            {order.files.zip}
                                        </Button>
                                    )}
                                    {order.files.errorLog && (
                                        <Button
                                            type="link"
                                            size="small"
                                            href={`/api/download-peek-result/${run.id}/${order.orderId}/error.log`}
                                            target="_blank"
                                        >
                                            error.log
                                        </Button>
                                    )}
                                    {!order.files.resultHtml && !order.files.zip && !order.files.errorLog && (
                                        <span style={{ color: '#999' }}>No files yet</span>
                                    )}
                                </Space>
                            </Card>
                        ))
                    )}
                </Card>
            )}

            {isAdmin(userContext.user) && run && (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <Button
                        onClick={handleGetMuxScansFromReport}
                        loading={reportMuxChannelLoading}
                    >
                        Get Mux Scan Results from report
                    </Button>
                </div>
            )}

            {/* Saved Channel States Heatmap */}
            {isAdmin(userContext.user) && run.channel_states && (() => {
                let savedStates: any[] = []
                try {
                    const raw = run.channel_states
                    savedStates = Array.isArray(raw) ? raw : JSON.parse(raw as string)
                } catch { return null }
                if (!Array.isArray(savedStates) || savedStates.length === 0) return null

                const stateColor: Record<string, string> = {
                    strand: '#52c41a',
                    pore: '#1677ff',
                    adapter: '#69c0ff',
                    event: '#fadb14',
                    unblocking: '#fa8c16',
                    above: '#722ed1',
                    saturated: '#434343',
                    unavailable: '#bfbfbf',
                    unclassified: '#d9d9d9',
                    multiple: '#13c2c2',
                    zero: '#595959',
                    inactive: '#d9d9d9',
                }
                const cols = 32
                const cellSize = 12
                const gap = 1

                return (
                    <Card
                        title="Saved Channel States"
                        size="small"
                        style={{ marginTop: 12, marginBottom: 8 }}
                    >
                        {savedStates.map((cs: any, posIdx: number) => {
                            const channelMap: Record<number, string> = {}
                            const rawStatesSource = cs.channel_states ?? cs.states ?? []
                            const rawStates: any[] = Array.isArray(rawStatesSource)
                                ? rawStatesSource
                                : normalizeReportChannelStates(rawStatesSource)
                            rawStates.forEach((s: any) => {
                                const ch = s.channel ?? s.channel_number
                                const st = (s.state_name ?? s.name ?? 'unclassified').toLowerCase()
                                if (ch !== undefined) channelMap[ch] = st
                            })
                            const maxChannel = Math.max(512, ...Object.keys(channelMap).map(Number))
                            const rows = Math.ceil(maxChannel / cols)
                            const stateCounts: Record<string, number> = {}
                            Object.values(channelMap).forEach(s => { stateCounts[s] = (stateCounts[s] || 0) + 1 })

                            return (
                                <Card
                                    key={posIdx}
                                    type="inner"
                                    size="small"
                                    title={
                                        <span>
                                            Position: <strong>{cs.positionName || 'unknown'}</strong>
                                            {cs.savedAt && (
                                                <span style={{ marginLeft: 12, fontWeight: 'normal', color: '#666', fontSize: 12 }}>
                                                    Saved: {new Date(cs.savedAt).toLocaleString()}
                                                </span>
                                            )}
                                        </span>
                                    }
                                    style={{ marginBottom: posIdx < savedStates.length - 1 ? 12 : 0 }}
                                >
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                                        {Object.entries(stateColor).filter(([s]) => stateCounts[s]).map(([state, color]) => (
                                            <span key={state} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                                <span style={{ width: 12, height: 12, background: color, display: 'inline-block', borderRadius: 2 }} />
                                                {state} ({stateCounts[state] || 0})
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <svg
                                            width={cols * (cellSize + gap) - gap}
                                            height={rows * (cellSize + gap) - gap}
                                            style={{ display: 'block' }}
                                        >
                                            {Array.from({ length: maxChannel }, (_, i) => {
                                                const ch = i + 1
                                                const state = channelMap[ch] ?? 'unclassified'
                                                const color = stateColor[state] ?? stateColor.unclassified
                                                const col = i % cols
                                                const row = Math.floor(i / cols)
                                                return (
                                                    <rect key={ch} x={col * (cellSize + gap)} y={row * (cellSize + gap)} width={cellSize} height={cellSize} fill={color} rx={1}>
                                                        <title>Ch {ch}: {state}</title>
                                                    </rect>
                                                )
                                            })}
                                        </svg>
                                    </div>
                                </Card>
                            )
                        })}
                    </Card>
                )
            })()}
            {/* RBK Run Section - Admin Only */}
            {isAdmin(userContext.user) && run && (
                <Card 
                    title="Start RBK Sequencing Run" 
                    size="small" 
                    style={{ marginTop: 24, marginBottom: 24 }}
                ><div style={{ marginTop: 8,marginBottom:16, fontSize: '0.9em', color: '#666' }}>
                                Run ID: <strong>{run.id}</strong> | Output: /data/plab/runs
                            </div>
                    <Space direction="horizontal" style={{ width: '100%', flexWrap: 'wrap' }} size="middle">
                        <div>
                            <label style={{ display: 'inline', marginBottom: 8 }}>
                                <strong>Device:</strong>
                            </label>
                            <Space.Compact>
                                <Select
                                    value={rbkDevice}
                                    onChange={setRbkDevice}
                                    style={{ width: 120 }}
                                    options={[
                                        { value: 'a6000', label: 'a6000' },
                                        { value: 'mk1c', label: 'mk1c' },
                                        { value: 'outpond', label: 'outpond' },
                                        { value: 'sierra', label: 'Sierra' }
                                    ]}
                                />
                                {availablePositions.length > 0 && (
                                    positionLockedByFlowcell ? (
                                        <Tag color="blue" style={{ display: 'flex', alignItems: 'center', margin: 0, borderRadius: '0 6px 6px 0', padding: '0 12px', fontSize: 14 }}>
                                            {rbkPosition}
                                        </Tag>
                                    ) : (
                                        <Select
                                            placeholder="Position"
                                            value={rbkPosition || undefined}
                                            onChange={setRbkPosition}
                                            style={{ minWidth: 140 }}
                                            options={availablePositions.map(p => ({ label: p, value: p }))}
                                        />
                                    )
                                )}
                            </Space.Compact>
                        </div>
                        <div>
                            <label style={{ display: 'inline', marginBottom: 8 }}>
                                <strong>Duration (hours):</strong>
                            </label>
                            <InputNumber
                                value={rbkHours}
                                onChange={(val) => setRbkHours(val)}
                                placeholder="e.g., 4"
                                min={0}
                                step={0.5}
                                style={{ width: 200 }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <Button
                                type="primary"
                                onClick={handleStartRbkRun}
                                loading={rbkRunning}
                                disabled={!rbkHours || rbkRunning}
                            >
                                Start RBK Run
                            </Button>
                            <Button
                                onClick={handleGetAquisitionInfo}
                                loading={acquisitionLoading}
                                disabled={rbkRunning}
                            >
                                Get Aquisition Info
                            </Button>
                            <Button
                                onClick={handleGetChannelStates}
                                loading={channelStatesLoading}
                                disabled={rbkRunning}
                            >
                                Channel States
                            </Button>
                            {(run?.status === 'running' || run?.status === 'new') && (
                                <Button
                                    danger
                                    onClick={handleStopProtocol}
                                    loading={stopProtocolLoading}
                                    disabled={rbkRunning}
                                >
                                    Stop Protocol
                                </Button>
                            )}
                        </div>
                    </Space>


                    {/* Change Run-Until Section */}
                    <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                        <div style={{ marginBottom: 8, fontSize: '0.9em', fontWeight: 'bold' }}>Update Run-Until (for running protocols):</div>
                        <Space>
                            <InputNumber
                                value={runUntilHours}
                                onChange={(val) => setRunUntilHours(val)}
                                placeholder="e.g., 3.5"
                                min={0}
                                step={0.1}
                                style={{ width: 150 }}
                            />
                            <span>hours</span>
                            <Button
                                onClick={handleUpdateRunUntil}
                                disabled={!runUntilHours}
                            >
                                Change Run-Until
                            </Button>
                        </Space>
                        <div style={{ marginTop: 8, fontSize: '0.85em', color: '#666' }}>
                            Use this to extend or shorten a currently running protocol
                        </div>
                    </div>

                    {acquisitionError && (
                        <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: '0.9em' }}>
                            {acquisitionError}
                        </div>
                    )}

                    {acquisitionData && (
                        <Card title="Aquisition Info" size="small" style={{ marginTop: 16, marginBottom: 8 }}>
                            <Descriptions bordered size="small" column={1}>
                                {acquisitionData.run_id && (
                                    <Descriptions.Item label="Run ID">{acquisitionData.run_id}</Descriptions.Item>
                                )}
                                {isAdmin(userContext.user) && (acquisitionData.reads_directory || acquisitionData.config_summary?.reads_directory) && (
                                    <Descriptions.Item label="Reads Directory">
                                        {acquisitionData.reads_directory || acquisitionData.config_summary?.reads_directory}
                                    </Descriptions.Item>
                                )}
                                {acquisitionData.state && (
                                    <Descriptions.Item label="State">
                                        <Tag color={acquisitionData.state === 'ACQUISITION_RUNNING' ? 'green' : acquisitionData.state === 'ACQUISITION_COMPLETED' ? 'blue' : 'default'}>
                                            {acquisitionData.state}
                                        </Tag>
                                    </Descriptions.Item>
                                )}
                                {acquisitionData.stop_reason && (
                                    <Descriptions.Item label="Stop Reason">{acquisitionData.stop_reason}</Descriptions.Item>
                                )}
                                {acquisitionData.yield_summary?.read_count !== undefined && (
                                    <Descriptions.Item label="Read Count">
                                        {acquisitionData.yield_summary.read_count.toLocaleString()}
                                    </Descriptions.Item>
                                )}
                                {acquisitionData.start_time && (
                                    <Descriptions.Item label="Hours Running">
                                        {(() => {
                                            const totalMinutes = Math.floor((Date.now() - (parseInt(acquisitionData.start_time.seconds) * 1000 + (acquisitionData.start_time.nanos || 0) / 1000000)) / (1000 * 60))
                                            const hours = Math.floor(totalMinutes / 60)
                                            const minutes = totalMinutes % 60
                                            return `${hours}h ${minutes}m`
                                        })()}
                                    </Descriptions.Item>
                                )}
                                {acquisitionData.start_time && acquisitionData.yield_summary?.read_count !== undefined && run?.nSamples && (
                                    <Descriptions.Item label="Recommended Run Time">
                                        {(() => {
                                            const totalMinutes = Math.floor((Date.now() - (parseInt(acquisitionData.start_time.seconds) * 1000 + (acquisitionData.start_time.nanos || 0) / 1000000)) / (1000 * 60))
                                            const readCount = acquisitionData.yield_summary.read_count
                                            const sampleCount = run.nSamples as number
                                            const kReadsPerMinute = readCount > 0 && totalMinutes > 4 ? (readCount / 1000) / (totalMinutes - 4) : 0
                                            const recommendedMinutes = kReadsPerMinute > 0 && sampleCount > 0 ? (sampleCount * 1) / kReadsPerMinute : 0
                                            if (recommendedMinutes <= 0) return 'N/A'
                                            const recHours = Math.floor(recommendedMinutes / 60)
                                            const recMins = Math.round(recommendedMinutes % 60)
                                            return `${recHours}h ${recMins}m (${(recommendedMinutes / 60).toFixed(2)} hrs)`
                                        })()}
                                    </Descriptions.Item>
                                )}
                                {acquisitionData.target_run_until_criteria && (() => {
                                    const runtime = acquisitionData.target_run_until_criteria?.stop_criteria?.criteria?.runtime
                                    if (runtime?.value?.data) {
                                        const runtimeData = runtime.value.data
                                        let value = 0
                                        let shift = 0
                                        for (let i = 1; i < runtimeData.length; i++) {
                                            value |= (runtimeData[i] & 0x7F) << shift
                                            if ((runtimeData[i] & 0x80) === 0) break
                                            shift += 7
                                        }
                                        const hours = (value / 3600).toFixed(2)
                                        return (
                                            <Descriptions.Item label="Run-until Criteria">
                                                {hours} hours
                                            </Descriptions.Item>
                                        )
                                    }
                                    return null
                                })()}
                            </Descriptions>
                            {acquisitionData.bream_info?.mux_scan_results && acquisitionData.bream_info.mux_scan_results.length > 0 && (
                                <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
                                    <span style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Mux Scan Results - Single Pore Counts:</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {acquisitionData.bream_info.mux_scan_results.map((result: any, idx: number) => (
                                            <Tag key={idx} color="green" style={{ margin: 0 }}>
                                                {result.counts.single_pore}
                                            </Tag>
                                        ))}
                                    </div>
                                    <span style={{ color: '#666', fontSize: '0.85em', marginTop: 8, display: 'block' }}>
                                        Total scans: {acquisitionData.bream_info.mux_scan_results.length} |
                                        {' '}Latest: {acquisitionData.bream_info.mux_scan_results[acquisitionData.bream_info.mux_scan_results.length - 1]?.counts.single_pore} pores
                                    </span>
                                </div>
                            )}
                        </Card>
                    )}
                    
                    {channelStatesError && (
                        <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: '0.9em' }}>
                            {channelStatesError}
                        </div>
                    )}

                    {channelStatesData && (() => {
                        return (
                            <Card
                                title="Channel States"
                                size="small"
                                style={{ marginTop: 16, marginBottom: 8 }}
                                extra={
                                    <Button size="small" onClick={() => setChannelStatesData(null)}>Close</Button>
                                }
                            >
                                {channelStatesData.map((channelStates: any, posIdx: number) => {
                                    return (
                                        <Card
                                            key={posIdx}
                                            type="inner"
                                            title={`Position: ${channelStates.positionName}`}
                                            size="small"
                                            style={{ marginBottom: posIdx < channelStatesData.length - 1 ? 12 : 0 }}
                                        >
                                            <details>
                                                <summary style={{ cursor: 'pointer' }}>Full JSON</summary>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(JSON.stringify(channelStates, null, 2))
                                                        antdMessage.success('JSON copied to clipboard!')
                                                    }}
                                                    style={{ marginTop: 8, marginBottom: 8 }}
                                                >
                                                    Copy JSON
                                                </Button>
                                                <pre style={{ fontSize: '0.85em', marginTop: 8 }}>{JSON.stringify(channelStates, null, 2)}</pre>
                                            </details>
                                        </Card>
                                    )
                                })}
                            </Card>
                        )
                    })()}
                    
                    {flowcellCheckError && (
                        <div style={{ marginTop: 8, padding: 12, backgroundColor: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4, position: 'relative' }}>  
                            <button
                                onClick={() => setFlowcellCheckError(null)}
                                style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    color: '#d46b08',
                                    padding: 0,
                                    lineHeight: 1
                                }}
                            >
                                ×
                            </button>
                            <div style={{ color: '#d46b08', fontSize: '0.9em', paddingRight: 20 }}>
                                {flowcellCheckError}
                            </div>
                        </div>
                    )}
                    <Button
                        type="default"
                        onClick={handleFlowcellCheckProgress}
                                    loading={flowcellCheckLoading}
                                    style={{ marginTop:16,marginBottom:8}}
                                >
                                    Show Flowcell Check Progress
                                </Button>
                    {rbkError && (
                                <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: '0.9em' }}>
                                    {rbkError}
                                </div>
                            )}
                    {/* Flowcell Check Progress Display */}
                    {flowcellCheckData && (
                        <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f0f2f5', borderRadius: 4 }}>
                            <h4 style={{ marginTop: 0, marginBottom: 12 }}>Flowcell Check Progress</h4>
                            {flowcellCheckData.state && (
                                <div style={{ marginBottom: 8 }}>
                                    <strong>State:</strong> {flowcellCheckData.state}
                                </div>
                            )}
                            {flowcellCheckData.pqc_result && (
                                <>
                                    <div style={{ marginBottom: 8 }}>
                                        <strong>Flow Cell ID:</strong> {flowcellCheckData.pqc_result.flow_cell_id}
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <strong>Passed:</strong> {flowcellCheckData.pqc_result.passed ? '✓ Yes' : '✗ No'}
                                    </div>
                                    <div style={{ marginBottom: 8, fontSize: '1.2rem', color: '#28a745' }}>
                                        <strong>Available Pores:</strong> {flowcellCheckData.pqc_result.total_pore_count?.toLocaleString() || 'N/A'}
                                    </div>
                                </>
                            )}
                            {flowcellCheckData.phase_history && flowcellCheckData.phase_history.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                    <strong>Phase History:</strong>
                                    <div style={{ marginLeft: '1rem', marginTop: 8 }}>
                                        {flowcellCheckData.phase_history.map((phaseEntry: any, idx: number) => (
                                            <div key={idx} style={{ marginBottom: 4, fontSize: '0.9rem' }}>
                                                <span style={{ color: '#666' }}>
                                                    {new Date(parseInt(phaseEntry.timestamp?.seconds || 0) * 1000).toLocaleString()}
                                                </span>
                                                {' → '}
                                                <span style={{ fontWeight: 'bold' }}>{phaseEntry.phase}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {!flowcellCheckData.pqc_result && (flowcellCheckData.state === 'PROTOCOL_RUNNING' || flowcellCheckData.protocol_state === 'PROTOCOL_RUNNING') && (
                                <div style={{ marginBottom: 8, color: '#ffc107' }}>
                                    ⏳ Flowcell check is still running... Results will be available when complete.
                                </div>
                            )}
                        </div>
                    )}
                </Card>
            )}
            {run.modifications && (
                <div style={{ marginTop: "2em", color: "#403e3e" }}>
                    <b>Modifications:</b>
                    <div style={{ whiteSpace: "pre-line", fontSize: 13, color: "#403e3e", marginTop: 4 }}>
                        {run.modifications
                            .split(";")
                            .filter((line) => line.trim().length > 0)
                            .map((line, idx) => (
                                <div key={idx}>{line.trim()};</div>
                            ))}
                    </div>
                </div>
            )}
            
            {/* Instrument Reports Section */}
            {isAdmin(userContext.user) && (
                <div style={{ marginTop: "2.5em" }}>
                    <h3 style={{ marginBottom: 16 }}>Instrument Reports on Web Server</h3>
                    {reportsLoading ? (
                        <div>Loading reports...</div>
                    ) : reportsError ? (
                        <div style={{ color: "red" }}>{reportsError}</div>
                    ) : reports.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
                            {reports.map((report, idx) => (
                                <Button
                                    key={idx}
                                    type="link"
                                    onClick={() => window.open(`/api/runs/report-html/${runId}/${report}`, "_blank")}
                                    style={{ textAlign: "left", padding: 0 }}
                                >
                                    {report}
                                </Button>
                            ))}
                        </div>
                    ) : (
                        <div style={{ color: "#888" }}>No instrument reports found</div>
                    )}
                </div>
            )}

            <Space size="large" style={{ marginTop: "2.5em" }}>
                {/* Show Run Settings Button */}
                <Button type="default" style={{ marginBottom: 16 }} onClick={handleShowSettings}>
                    Show Run Settings
                </Button>
                <Button
                    type="default" style={{ marginBottom: 16 }} onClick={handleRegenerateSettings}
                >
                    Regenerate Run Settings
                </Button>
            </Space>
            {/* Notes at the bottom */}
            <div style={{ padding: 16, background: "#f9f9f9", borderRadius: 8, color: "#888" }}>
                <b>Notes:</b>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li>
                        When an order is changed to "canceled", the plates will still point to this run until admin
                        assigns another run to them.
                    </li>
                    <li>
                        data path on mac: /Library/MinKNOW/data/
                    </li>
                    <li>
                        To prevent macos from sleeping during a run, open a ternimal and type "caffeinate -i"  and hit enter
                    </li>

                </ul>
            </div>
            {/* Show pretty JSON settings at the bottom if available */}
            {settings && (
                <div style={{ marginTop: 24 }}>
                    <b>Run Settings:</b>
                    <pre
                        style={{
                            background: "#f6f6f6",
                            padding: 12,
                            borderRadius: 6,
                            
                            overflow: "auto",
                            color: "#222",
                        }}
                    >
                        {(() => {
                            try {
                                return JSON.stringify(
                                    typeof settings === "string" ? JSON.parse(settings) : settings,
                                    null,
                                    2
                                )
                            } catch {
                                return settings
                            }
                        })()}
                    </pre>
                </div>
            )}

            {/* Basecaller Log Summary Modal */}
            <Modal
                title={`Log Summary: ${summarizeModalFileName}`}
                open={summarizeModalOpen}
                onCancel={() => setSummarizeModalOpen(false)}
                footer={<Button onClick={() => setSummarizeModalOpen(false)}>Close</Button>}
                width={600}
            >
                {summarizeModalLoading && <div style={{ textAlign: 'center', padding: 24 }}>Loading...</div>}
                {summarizeModalError && <div style={{ color: '#ff4d4f' }}>{summarizeModalError}</div>}
                {summarizeModalData && !summarizeModalLoading && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Simplex reads</div>
                            {summarizeModalData.simplexBasecalled.length === 0 && summarizeModalData.simplexFiltered.length === 0 ? (
                                <span style={{ color: '#999' }}>Not found</span>
                            ) : (
                                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9em' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f5f5' }}>
                                            <th style={{ padding: '4px 12px', textAlign: 'left', border: '1px solid #e8e8e8' }}>#</th>
                                            <th style={{ padding: '4px 12px', textAlign: 'right', border: '1px solid #e8e8e8' }}>Basecalled</th>
                                            <th style={{ padding: '4px 12px', textAlign: 'right', border: '1px solid #e8e8e8' }}>Filtered</th>
                                            <th style={{ padding: '4px 12px', textAlign: 'right', border: '1px solid #e8e8e8' }}>Unclassified</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: Math.max(summarizeModalData.simplexBasecalled.length, summarizeModalData.simplexFiltered.length, summarizeModalData.simplexUnclassified.length) }, (_, i) => (
                                            <tr key={i}>
                                                <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8', color: '#888' }}>{i + 1}</td>
                                                <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8', textAlign: 'right' }}>
                                                    {summarizeModalData.simplexBasecalled[i] != null ? summarizeModalData.simplexBasecalled[i].toLocaleString() : '—'}
                                                </td>
                                                <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8', textAlign: 'right' }}>
                                                    {summarizeModalData.simplexFiltered[i] != null ? summarizeModalData.simplexFiltered[i].toLocaleString() : '—'}
                                                </td>
                                                <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8', textAlign: 'right', color: '#888' }}>
                                                    {summarizeModalData.simplexUnclassified[i] != null ? summarizeModalData.simplexUnclassified[i].toLocaleString() : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr style={{ fontWeight: 600, background: '#fafafa' }}>
                                            <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8' }}>Total</td>
                                            <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8', textAlign: 'right' }}>
                                                {summarizeModalData.simplexBasecalled.reduce((s, v) => s + v, 0).toLocaleString()}
                                            </td>
                                            <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8', textAlign: 'right' }}>
                                                {summarizeModalData.simplexFiltered.reduce((s, v) => s + v, 0).toLocaleString()}
                                            </td>
                                            <td style={{ padding: '3px 12px', border: '1px solid #e8e8e8', textAlign: 'right', color: '#888' }}>
                                                {summarizeModalData.simplexUnclassified.reduce((s, v) => s + v, 0).toLocaleString()}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Barcode distribution (SQK-RBK114-96)</div>
                            {summarizeModalData.barcodeCounts.length === 0 ? (
                                <span style={{ color: '#999' }}>No barcode entries found</span>
                            ) : (
                                <>
                                    <div style={{ marginBottom: 8, fontWeight: 500 }}>
                                        Total: {summarizeModalData.barcodeTotal.toLocaleString()}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px 16px', fontSize: '0.85em' }}>
                                        {summarizeModalData.barcodeCounts.map((b, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#555' }}>{b.name.replace('SQK-RBK114-96_', '')}</span>
                                                <span>{b.count.toLocaleString()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </ContainerFull>
    )
}

export default RunDetails
